import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import type { SummaryScales, UseQueryWrapper } from 'app/types';
import type { GetSummaryScalesArgs } from 'app/Domain/Settings/Services';
import { settingsServices } from 'app/Domain/Settings/Services';

import { useSnakeBar } from '../../useSnakeBar';

export type GetAllSummaryScalesQueryVariables = GetSummaryScalesArgs;

export type GetAllSummaryScalesQueryResultData = {
  data: SummaryScales[];
  totalCount: number;
};

const queryFn = (variables?: GetAllSummaryScalesQueryVariables) => async () => {
  if (variables?.ownerId) {
    const response = await settingsServices.getSummaryScales(variables);
    return (response as AxiosResponse).data;
  }
};

const QUERY_KEY = 'getSummaryScales';

export const useSettingsGetAllSummaryScalesQuery: UseQueryWrapper<
  GetAllSummaryScalesQueryResultData,
  GetAllSummaryScalesQueryVariables
> = ({ variables, options }) => {
  const { showErrorSnakeBar } = useSnakeBar();

  const onError = React.useCallback(() => {
    showErrorSnakeBar({ method: QUERY_KEY });
  }, [showErrorSnakeBar]);

  const select = React.useCallback(data => {
    return {
      data: data['hydra:member'],
      totalCount: data['hydra:totalItems'],
    };
  }, []);

  return useQuery<GetAllSummaryScalesQueryResultData>(
    [QUERY_KEY, ...Object.values(variables || {})],
    queryFn(variables),
    {
      select,
      onError,
      ...(options || {}),
    },
  );
};
